import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { formatRupiah, formatTanggal } from "../lib/format";
import { ArrowLeft, FileText, Truck, PackageCheck, Wrench, Loader2, Users2 } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "../components/ui/progress";

const STATUS_TONE = {
    aktif: "bg-emerald-50 text-emerald-700 border-emerald-200",
    selesai: "bg-blue-50 text-blue-700 border-blue-200",
    dibatalkan: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function SPKDetailPage() {
    const { id } = useParams();
    const nav = useNavigate();
    const [spk, setSpk] = useState(null);
    const [bonKain, setBonKain] = useState([]);
    const [bonPeralatan, setBonPeralatan] = useState([]);
    const [setor, setSetor] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [s, bk, bp, sr] = await Promise.all([
                    api.get(`/spk/${id}`),
                    api.get(`/bon-kain`, { params: { spk_id: id } }),
                    api.get(`/bon-peralatan`, { params: { spk_id: id } }),
                    api.get(`/setor-barang`, { params: { spk_id: id } }),
                ]);
                setSpk(s.data); setBonKain(bk.data); setBonPeralatan(bp.data); setSetor(sr.data);
            } catch (e) { toast.error(errorMessage(e)); }
            finally { setLoading(false); }
        })();
    }, [id]);

    if (loading || !spk) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-teal-600 animate-spin" /></div>;

    const s = spk.summary || {};

    return (
        <div className="space-y-6">
            <button onClick={() => nav("/spk")} className="text-sm text-slate-500 hover:text-teal-700 flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Kembali ke daftar SPK
            </button>

            <div className="glass-card rounded-2xl p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-widest text-teal-700/80">SPK / Job Order</div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight" data-testid="spk-detail-nomor">{spk.nomor_spk}</h2>
                        <Link to={`/vendor/${spk.vendor_id}`} className="text-sm text-slate-600 hover:text-teal-700 flex items-center gap-1.5 mt-1">
                            <Users2 className="w-3.5 h-3.5" /> {spk.vendor?.nama || "-"}
                        </Link>
                        <div className="text-xs text-slate-500 mt-1">Tanggal: {formatTanggal(spk.tanggal_spk)}{spk.deadline && ` · Deadline: ${formatTanggal(spk.deadline)}`}</div>
                    </div>
                    <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${STATUS_TONE[spk.status]}`}>{spk.status}</span>
                </div>
                {spk.catatan && <div className="mt-4 p-3 rounded-xl bg-slate-50 text-sm text-slate-700 border border-slate-200/70">{spk.catatan}</div>}
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Kain Masuk" value={`${(s.total_yard_masuk || 0).toLocaleString("id-ID")} yard`} icon={Truck} tone="indigo" />
                <StatCard label="Nilai Bon Kain" value={formatRupiah(s.total_nilai_bon_kain || 0)} icon={Truck} tone="indigo" />
                <StatCard label="Nilai Bon Peralatan" value={formatRupiah(s.total_nilai_bon_peralatan || 0)} icon={Wrench} tone="purple" />
                <StatCard label="Nilai Setor Barang" value={formatRupiah(s.total_nilai_setor || 0)} icon={PackageCheck} tone="emerald" />
            </div>

            {/* Progress items */}
            <div className="glass-card rounded-2xl p-5">
                <div className="text-lg font-bold text-slate-900 mb-3">Progress Produksi</div>
                <div className="space-y-3">
                    {spk.progress_items?.map((it) => (
                        <div key={it.produk_id} data-testid={`spk-detail-progress-${it.produk_id}`} className="p-4 rounded-xl bg-slate-50/70 border border-slate-200/60">
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-slate-900 truncate">{it.nama_produk_snapshot}</div>
                                    <div className="text-xs text-slate-500 num-tabular">Harga sepakat: {formatRupiah(it.harga_per_pcs_sepakat)} / pcs</div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className="text-sm font-bold text-slate-900 num-tabular">{it.setor_pcs.toLocaleString("id-ID")} / {it.target_pcs.toLocaleString("id-ID")} pcs</div>
                                    <div className="text-xs text-slate-500">Sisa {it.sisa_pcs.toLocaleString("id-ID")} pcs</div>
                                </div>
                            </div>
                            <Progress value={Math.min(100, it.progress_pct)} className="h-2 bg-slate-200" />
                            <div className="text-right text-[11px] text-slate-500 mt-1 num-tabular">{it.progress_pct.toFixed(1)}%</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Related transactions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <RelatedTx title="Bon Kain" icon={Truck} rows={bonKain} cols={[
                    { key: "nomor_bon", label: "Nomor" },
                    { key: "tanggal", label: "Tanggal", format: "date" },
                    { key: "total_nilai", label: "Nilai", format: "currency" },
                ]} />
                <RelatedTx title="Bon Peralatan" icon={Wrench} rows={bonPeralatan} cols={[
                    { key: "nomor_bon", label: "Nomor" },
                    { key: "tanggal", label: "Tanggal", format: "date" },
                    { key: "total_nilai", label: "Nilai", format: "currency" },
                ]} />
                <div className="lg:col-span-2">
                    <RelatedTx title="Setor Barang Jadi" icon={PackageCheck} rows={setor} cols={[
                        { key: "nomor_setor", label: "Nomor" },
                        { key: "tanggal_setor", label: "Tanggal", format: "date" },
                        { key: "total_pcs", label: "Pcs", format: "number", compute: (r) => r.items?.reduce((s, i) => s + Number(i.jumlah_pcs || 0), 0) || 0 },
                        { key: "total_nilai", label: "Nilai", format: "currency" },
                    ]} />
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, icon: Icon, tone }) {
    const TONE_MAP = {
        indigo: "bg-indigo-50 text-indigo-700 ring-indigo-100",
        purple: "bg-purple-50 text-purple-700 ring-purple-100",
        emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    };
    return (
        <div className="glass-card rounded-2xl p-4">
            <div className="flex items-start justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
                <div className={`w-8 h-8 rounded-lg ${TONE_MAP[tone]} ring-1 flex items-center justify-center`}><Icon className="w-3.5 h-3.5" /></div>
            </div>
            <div className="mt-2 text-lg font-extrabold text-slate-900 num-tabular">{value}</div>
        </div>
    );
}

function RelatedTx({ title, icon: Icon, rows, cols }) {
    return (
        <div className="glass-card rounded-2xl p-5">
            <div className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2"><Icon className="w-4 h-4 text-teal-600" /> {title} ({rows.length})</div>
            {rows.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">Belum ada transaksi</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200/60">
                                {cols.map((c) => <th key={c.key} className="text-left text-[10px] uppercase tracking-wider text-slate-500 py-1.5 px-2">{c.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-slate-100/60 hover:bg-teal-50/30">
                                    {cols.map((c) => {
                                        const raw = c.compute ? c.compute(r) : r[c.key];
                                        let val = raw;
                                        if (c.format === "date") val = formatTanggal(raw);
                                        else if (c.format === "currency") val = <span className="num-tabular font-semibold">{formatRupiah(raw || 0)}</span>;
                                        else if (c.format === "number") val = <span className="num-tabular">{Number(raw || 0).toLocaleString("id-ID")}</span>;
                                        return <td key={c.key} className="py-2 px-2">{val}</td>;
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
